import { keyframes } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface LoadingStateProps {
  text?: string;
  children?: ReactNode;
}

const wave = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
`;

const blink = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const LoadingState: FC<LoadingStateProps> = ({ children, text }) => {
  const { t } = useTranslation();
  const content = (children as ReactNode) || text || t('conversations.loading.default');
  const asString = typeof content === 'string' ? content : undefined;

  return (
    <Box className='mex:flex mex:w-full mex:items-center mex:justify-center mex:py-6'>
      {asString ? (
        <Typography variant='body2' className='mex:flex mex:items-center mex:text-gray-500'>
          {asString.split('').map((ch, idx) => (
            <Box
              key={`${ch}-${idx}-${asString.length}`}
              component='span'
              sx={{
                display: 'inline-block',
                animation: `${wave} 1.2s ease-in-out ${idx * 0.06}s infinite`
              }}
            >
              {ch}
            </Box>
          ))}
          <Box component='span' className='mex:ml-1'>
            <Box component='span' sx={{ animation: `${blink} 1.4s infinite` }}>
              .
            </Box>
            <Box component='span' sx={{ animation: `${blink} 1.4s 0.2s infinite` }}>
              .
            </Box>
            <Box component='span' sx={{ animation: `${blink} 1.4s 0.4s infinite` }}>
              .
            </Box>
          </Box>
        </Typography>
      ) : (
        <Typography variant='body2' className='mex:flex mex:items-center mex:text-gray-500'>
          <Box component='span' sx={{ animation: `${blink} 1.4s infinite` }}>
            .
          </Box>
          <Box component='span' sx={{ animation: `${blink} 1.4s 0.2s infinite` }}>
            .
          </Box>
          <Box component='span' sx={{ animation: `${blink} 1.4s 0.4s infinite` }}>
            .
          </Box>
        </Typography>
      )}
    </Box>
  );
};

export default LoadingState;
